import type { NavigationGuard } from 'vue-router';
import { exchangeOidcAuthCode, exchangeSamlAuthCode, getUserProfile } from '@/api/auth';
import { cancelOidcRefresh, scheduleOidcRefresh } from '@/composables/useOidcTokenRefresh';
import { useAppConfigStore } from '@/stores/appConfig';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';
import { ENVIRONMENT, PEQUITY_API_URL } from '@/types';
import {
  clearIsOidcSession,
  clearTokens,
  consumeOidcPostLoginRedirect,
  getCorrelationId,
  getUrlBase,
  setAccessToken,
  setIsOidcSession,
  setOidcPostLoginRedirect,
  setRefreshToken,
} from '@/utils/helpers';

/**
 * This guard redirects the user to the SSO login page if the company is using SSO.
 */
export const redirectToSsoLoginIfUsingSso: NavigationGuard = async (to, from) => {
  if (to.query.error) {
    return { name: 'oidc-error', query: { done: '1' } };
  }

  const appConfigStore = useAppConfigStore();

  // OIDC (AIM) SSO — global setting
  if (appConfigStore.shouldRedirectToOidc) {
    if (from.name === 'oidc-login') {
      return;
    }

    const redirect = to.query.redirect;
    if (redirect && typeof redirect === 'string') {
      setOidcPostLoginRedirect(redirect);
    }

    window.location.href =
      ENVIRONMENT === 'local' ? PEQUITY_API_URL + '/oidc/authorize/' : getUrlBase() + '/api/oidc/authorize/';
    return false;
  }

  // SAML SSO — per-tenant setting (only when on a tenant subdomain)
  if (!appConfigStore.showTenantFinder) {
    return;
  }

  const companyStore = useCompanyStore();

  if (!companyStore.company?.id) {
    await companyStore.fetchCompanies();
  }

  if (companyStore.isUsingSSO) {
    const authStore = useAuthStore();
    await authStore.setUserProfileAndLoggedInState();

    if (authStore.loggedIn) {
      return { name: 'home' };
    }

    window.location.href = getUrlBase() + '/api/saml2/login';
    return false;
  }
};

/**
 * This guard handles the SAML login flow.
 * It exchanges the one-time auth code for tokens via a secure POST request,
 * then sets them in local storage and redirects to the home page.
 */
export const handleSamlLoginRedirect: NavigationGuard = async (to, from, next) => {
  const code = to.query.code;

  if (!code || typeof code !== 'string') {
    return next({ name: 'welcome-back' });
  }

  clearTokens();

  const { data, error: exchangeError } = await exchangeSamlAuthCode(code);

  if (exchangeError || !data?.access_token || !data?.refresh_token) {
    return next({ name: 'welcome-back' });
  }

  clearIsOidcSession();
  cancelOidcRefresh();
  setAccessToken(data.access_token);
  setRefreshToken(data.refresh_token);

  const { error } = await getUserProfile();

  if (error) {
    return next({ name: 'welcome-back' });
  } else {
    return next({ name: 'home' });
  }
};

/**
 * This guard handles the Lyric → Pequity OIDC SSO login flow.
 * It exchanges the one-time auth code for tokens via a secure POST request,
 * then sets them in local storage and redirects to the home page.
 */
export const handleOidcLoginRedirect: NavigationGuard = async (to, from, next) => {
  const code = to.query.code;

  if (!code || typeof code !== 'string') {
    return next({ name: 'welcome-back' });
  }

  const { data, error: exchangeError } = await exchangeOidcAuthCode(code);

  if (exchangeError || !data?.access_token) {
    return next({ name: 'oidc-error' });
  }

  setAccessToken(data.access_token);
  setIsOidcSession();

  const authStore = useAuthStore();
  await authStore.setUserProfileAndLoggedInState();

  if (authStore.loggedIn) {
    scheduleOidcRefresh();
    const redirectPath = consumeOidcPostLoginRedirect();
    return redirectPath ? next(redirectPath) : next({ name: 'home' });
  } else {
    clearTokens();
    consumeOidcPostLoginRedirect();
    return next({ name: 'oidc-error' });
  }
};

export const goHomeIfNoCorrelationId: NavigationGuard = async (to, from, next) => {
  if (getCorrelationId() === null) {
    return next({ name: 'home' });
  }
  return next();
};

export const redirectIfNoTenantFinder: NavigationGuard = async () => {
  const appConfigStore = useAppConfigStore();

  if (!appConfigStore.showTenantFinder) {
    return { name: 'welcome-back' };
  }
};

export const redirectIfNoPublicLogin: NavigationGuard = async () => {
  const appConfigStore = useAppConfigStore();

  if (!appConfigStore.showPublicLogin) {
    return { name: 'welcome-back' };
  }
};

export const redirectToPublicLoginIfNeeded: NavigationGuard = async (to) => {
  const appConfigStore = useAppConfigStore();

  if (appConfigStore.showPublicLogin) {
    return { name: 'public-login', query: to.query };
  }
};

export const redirectToTenantFinderIfNoSubdomain: NavigationGuard = async () => {
  const appConfigStore = useAppConfigStore();

  if (!appConfigStore.showTenantFinder) {
    return;
  }

  const baseHostname = new URL(appConfigStore.frontendBaseUrl).hostname;

  if (window.location.hostname === baseHostname) {
    return { name: 'universal-login' };
  }
};
